import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import HomePage from "@/pages/HomePage";
import VendorPage from "@/pages/VendorPage";
import CartPage from "@/pages/CartPage";
import OrdersPage from "@/pages/OrdersPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/AdminPage";
import SearchPage from "@/pages/SearchPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import LoginPage from "@/pages/LoginPage";
import AdminRegistrationsPage from "@/pages/AdminRegistrationsPage";
import AdminOrdersPage from "@/pages/AdminOrdersPage";
import VendorRegistrationsPage from "@/pages/VendorRegistrationsPage";
import ReferPage from "@/pages/ReferPage";
import ProfitSharingPage from "@/pages/ProfitSharingPage";
import ReferralDetailsPage from "@/pages/ReferralDetailsPage";
import ReferralRegistrationsPage from "@/pages/ReferralRegistrationsPage";
import ReferralRedirectPage from "@/pages/ReferralRedirectPage";
import ReferralPerformancePage from "@/pages/ReferralPerformancePage";
import PartnerProgramDetailsPage from "@/pages/PartnerProgramDetailsPage";
import ProfitSharingRegistrationsPage from "@/pages/ProfitSharingRegistrationsPage";
import ProfitSharingPerformancePage from "@/pages/ProfitSharingPerformancePage";
import DeliveryPartnerRegisterPage from "@/pages/DeliveryPartnerRegisterPage";
import AdminDeliveryApplicationsPage from "@/pages/AdminDeliveryApplicationsPage";
import AdminReferOffersPage from "@/pages/AdminReferOffersPage";
import BulkImportMenuPage from "@/pages/BulkImportMenuPage";
import React from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const UserContext = createContext();

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    handleAuth();
    loadCart();
  }, []);

  const handleAuth = async () => {
    // Check for session_id in URL fragment (for legacy Emergent auth)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        if (response.data.success) {
          setUser(response.data.user);
          toast.success('Login successful!');
        }
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed');
      }
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      return;
    }
    
    // Check existing session (supports phone_users, users, and Phase 3 call-verified sessions)
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      // Handle Phase 3 auth/me response format: { authenticated: bool, user: {...} }
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
      } else if (response.data.id) {
        // Legacy format - direct user object
        setUser(response.data);
      }
    } catch (error) {
      console.log('No active session');
    }
    setLoading(false);
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem('foodmatrix_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const login = () => {
    // Redirect to OTP login page
    navigate('/login');
  };

  const logout = async () => {
    // Clear user state FIRST — ensures no stale session access
    setUser(null);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout API error:', error);
    }
    toast.success('Logged out successfully');
    navigate('/');
  };

  const addToCart = async (item, vendor, customizations = null) => {
    const existingItemIndex = cart.findIndex(c => 
      c.menu_item_id === item.id && 
      JSON.stringify(c.customizations) === JSON.stringify(customizations)
    );
    
    let newCart;
    if (existingItemIndex >= 0) {
      newCart = cart.map((c, idx) => 
        idx === existingItemIndex
          ? { ...c, quantity: c.quantity + 1 }
          : c
      );
    } else {
      newCart = [...cart, {
        menu_item_id: item.id,
        vendor_id: vendor.id,
        vendor_name: vendor.name,
        quantity: 1,
        price: item.price,
        name: item.name,
        image_url: item.image_url,
        customizations
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('foodmatrix_cart', JSON.stringify(newCart));
    toast.success('Added to cart', {
      action: {
        label: 'View Cart',
        onClick: () => navigate('/cart')
      }
    });
    
    // Track recently viewed
    if (user) {
      try {
        const recentlyViewed = [...new Set([vendor.id, ...(user.recently_viewed || [])])].slice(0, 10);
        await axios.put(`${API}/users/${user.id}`, { recently_viewed: recentlyViewed }, { withCredentials: true });
      } catch (error) {
        console.error('Error updating recently viewed:', error);
      }
    }
  };

  const updateCartQuantity = (menuItemId, customizations, quantity) => {
    if (quantity <= 0) {
      removeFromCart(menuItemId, customizations);
      return;
    }
    
    const newCart = cart.map(item => {
      const matchesId = item.menu_item_id === menuItemId;
      const matchesCustom = JSON.stringify(item.customizations) === JSON.stringify(customizations);
      return matchesId && matchesCustom ? { ...item, quantity } : item;
    });
    
    setCart(newCart);
    localStorage.setItem('foodmatrix_cart', JSON.stringify(newCart));
  };

  const removeFromCart = (menuItemId, customizations) => {
    const newCart = cart.filter(item => {
      const matchesId = item.menu_item_id === menuItemId;
      const matchesCustom = JSON.stringify(item.customizations) === JSON.stringify(customizations);
      return !(matchesId && matchesCustom);
    });
    
    setCart(newCart);
    localStorage.setItem('foodmatrix_cart', JSON.stringify(newCart));
    toast.success('Removed from cart');
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('foodmatrix_cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-green-600 font-semibold">Loading FoodMatrix...</p>
        </div>
      </div>
    );
  }

  // Route guard: redirects unauthenticated users to /login with return path
  const ProtectedRoute = ({ children }) => {
    if (!user) {
      const returnPath = location.pathname + location.search;
      return <Navigate to={`/login?redirect=${encodeURIComponent(returnPath)}`} replace />;
    }
    return children;
  };

  // Admin route guard: validates admin via phone-based is_admin flag
  // User is cleared FIRST to prevent stale session re-auth loops
  const AdminRoute = ({ children }) => {
    if (!user) {
      const returnPath = location.pathname + location.search;
      return <Navigate to={`/login?redirect=${encodeURIComponent(returnPath)}`} replace />;
    }
    if (!user.is_admin) {
      // Clear user IMMEDIATELY — prevents handleAuth from re-authenticating stale session
      setUser(null);
      // Fire-and-forget logout to clean backend session & cookie
      axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
    }
    return children;
  };

  return (
    <UserContext.Provider value={{ user, login, logout, cart, addToCart, updateCartQuantity, removeFromCart, clearCart }}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={
          user
            ? <Navigate to={new URLSearchParams(location.search).get('redirect') || '/'} replace />
            : <LoginPage />
        } />
        <Route path="/vendor/:vendorId" element={<VendorPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/r/:referralCode" element={<ReferralRedirectPage />} />

        {/* Protected routes */}
        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/refer" element={<ProtectedRoute><ReferPage /></ProtectedRoute>} />
        <Route path="/profit-sharing" element={<ProtectedRoute><ProfitSharingPage /></ProtectedRoute>} />
        <Route path="/partner-program/:programType" element={<ProtectedRoute><PartnerProgramDetailsPage /></ProtectedRoute>} />
        <Route path="/referral-details" element={<ProtectedRoute><ReferralDetailsPage /></ProtectedRoute>} />
        <Route path="/track/:orderId" element={<ProtectedRoute><OrderTrackingPage /></ProtectedRoute>} />
        <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
        <Route path="/delivery-partner/register" element={<ProtectedRoute><DeliveryPartnerRegisterPage /></ProtectedRoute>} />

        {/* Admin routes (admin role required) */}
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/admin/registrations" element={<AdminRoute><AdminRegistrationsPage /></AdminRoute>} />
        <Route path="/admin/orders" element={<AdminRoute><AdminOrdersPage /></AdminRoute>} />
        <Route path="/admin/vendor-registrations" element={<AdminRoute><VendorRegistrationsPage /></AdminRoute>} />
        <Route path="/admin/vendor-registrations/:vendorId/bulk-import" element={<AdminRoute><BulkImportMenuPage /></AdminRoute>} />
        <Route path="/admin/referral-registrations" element={<AdminRoute><ReferralRegistrationsPage /></AdminRoute>} />
        <Route path="/admin/referral-registrations/:referralCode" element={<AdminRoute><ReferralPerformancePage /></AdminRoute>} />
        <Route path="/admin/profit-sharing-registrations" element={<AdminRoute><ProfitSharingRegistrationsPage /></AdminRoute>} />
        <Route path="/admin/profit-sharing-registrations/:referralCode" element={<AdminRoute><ProfitSharingPerformancePage /></AdminRoute>} />
        <Route path="/admin/delivery-applications" element={<AdminRoute><AdminDeliveryApplicationsPage /></AdminRoute>} />
        <Route path="/admin/refer-offers" element={<AdminRoute><AdminReferOffersPage /></AdminRoute>} />
      </Routes>
    </UserContext.Provider>
  );
}

function App() {
  return (
    <div className="App w-full max-w-full overflow-x-hidden">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;